import { getAudioContext } from '../utils/audio-context';
import { IS_FIREFOX } from '../utils/environment';
import loggerManager from '../utils/log/logger-manager';
import kvStatManager, { KV_REPORT_KEY_AUDIO_MANAGER } from './kv-stat-manager';
// 音频上行与否取决于LocalAudioTrack 和 ScreenAudioTrack 是否上行

// localAudioTrack -> MediaStreamSource -> denoiser -> gainNode? --|
// musicAudioTrack1 -> MediaStreamSource ->            gainNode? --| ---> destination -> mediaStreamTrack
// musicAudioTrack2 -> MediaStreamSource ->            gainNode? --|
export type AudioSource = HTMLAudioElement | MediaStreamAudioTrack | AudioNode;
// 用于动态插入或者移除节点的容器，实现动态连接
export class AudioNodeConnector<T extends AudioNode = AudioNode, T2 extends AudioNode = AudioNode> {
  node?: T;
  node2?: T2;
  pre = new Set<AudioNodeConnector>();
  next = new Set<AudioNodeConnector>();
  context?: MixContext;
  connectedNodes = new Set<AudioNode>();
  nextInputChannelMap = new Map<AudioNodeConnector, number>();
  private _channelCount = 1;
  public get channelCount() {
    return this._channelCount;
  }
  public set channelCount(value) {
    this._channelCount = value;
    this.setChannelCount(this.node, value);
    this.setChannelCount(this.node2, value);
    this.next.forEach(data => data.channelCount = value);
  }
  constructor(public name?: string) {

  }
  setChannelCount(node?: AudioNode, value?: number) {
    // ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/1020396022123690187
    if (!node || node instanceof ScriptProcessorNode) return;
    node.channelCountMode = 'explicit';
    node.channelCount = value || this.channelCount || 1;
  }
  setContext(context: MixContext) {
    this.context = context;
    if (this.node) context.addMixWeight();
  }
  removeContext() {
    if (this.node) this.context?.reduceMixWeight();
    delete this.context;
  }
  replaceNode(node: T) {
    if (node === this.node) return;
    try {
      if (this.node) this._disconnect();
      else this.context?.addMixWeight();
      this.node = node;
      this.setChannelCount(this.node);
      this.preNodeReconnect();
      this.reconnect();
    } catch (error) {
      loggerManager.error(error);
    }
  }
  setNode(node: T, node2?: T2) {
    if (this.node) return;
    try {
      this.context?.addMixWeight();
      this.node = node;
      this.setChannelCount(this.node);
      if (node2) {
        // node和 node2 之间存在若干个node 需要在外部插件里完成 connect
        this.node2 = node2;
        this.setChannelCount(this.node2);
      }
      this.preNodeReconnect();
      this.reconnect();
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_AUDIO_MANAGER.SET_NODE });
    } catch (error) {
      loggerManager.error(error);
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_AUDIO_MANAGER.SET_NODE, error });
    }
  }
  deleteNode() {
    if (!this.node) return;
    try {
      this._disconnect();
      delete this.node;
      delete this.node2;
      this.context?.reduceMixWeight();
      this.preNodeReconnect();
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_AUDIO_MANAGER.DELETE_NODE });
    } catch (error) {
      loggerManager.error(error);
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_AUDIO_MANAGER.DELETE_NODE, error });
    }
  }
  preNodeReconnect() {
    this.pre.forEach(input => {
      if (input.node) input.reconnect();
      else input.preNodeReconnect();
    });
  }
  connectNext(node: AudioNodeConnector) {
    this.next.forEach(data => {
      const inputIndex = this.nextInputChannelMap.get(data);
      node._connect(data.node, inputIndex) || data.connectNext(node);
    });
  }
  _connect(node?: AudioNode, inputIndex = 0) {
    if (!this.node || !node) return false;
    // 对于有node2的情况，connectNext 需要用node2进行connect
    (this.node2 || this.node).connect(node, 0, inputIndex);
    this.connectedNodes.add(node);
    return true;
  }
  _disconnect() {
    this.connectedNodes.forEach(node => (this.node2 || this.node)?.disconnect(node));
    this.connectedNodes.clear();
  }
  reconnect() {
    this._disconnect();
    this.connectNext(this);
  }
  pipeTo(destinationNode: AudioNodeConnector, inputIndex = 0) {
    this.next.add(destinationNode);
    destinationNode.pre.add(this);
    this.nextInputChannelMap.set(destinationNode, inputIndex);
    return destinationNode;
  }
}
export class AnalyserNodeConnector extends AudioNodeConnector<AnalyserNode> {
  dataArray = new Uint8Array(0);
  constructor(public fftSize = 256) {
    super();
  }
  setNode(node: AnalyserNode) {
    node.fftSize = this.fftSize;
    this.dataArray = new Uint8Array(node.frequencyBinCount);
    super.setNode(node);
  }
  getByteTimeDomainData() {
    this.node?.getByteTimeDomainData(this.dataArray);
    return this.dataArray;
  }
  get level() {
    this.node?.getByteTimeDomainData(this.dataArray);
    return Math.max(...this.dataArray) / 128.0 - 1.0;
    // let sum = 0.0;
    // for (let i = 0; i < this.dataArray.length; ++i) {
    //   const s = this.dataArray[i] / 128.0 - 1.0;
    //   sum += s * s;
    // }
    // return Math.sqrt(sum / this.dataArray.length) || 0.0;
  }
  // 返回 svg path data
  get timeDomainPathData() {
    const dataArray = this.getByteTimeDomainData();
    const sliceWidth = 1;
    let x = 0;
    let y = 0;
    let path = `M${x},${y}`;
    for (let i = 0; i < dataArray.length; i++) {
      const v = dataArray[i] / 128.0;
      y = (v * 100) / 2;
      path += `L${x},${y}`;
      x += sliceWidth;
    }
    return path;
  }
}

export class AudioPipeline {
  source = new AudioNodeConnector();
  gain = new AudioNodeConnector<GainNode>();
  destination = new AudioNodeConnector<MediaStreamAudioDestinationNode>();
  get volume() {
    return this.gain.node?.gain.value || 1;
  }
  setVolume(volume: number) {
    if (volume === 1) {
      if (this.gain.node) this.gain.deleteNode();
      return;
    }
    if (!this.gain.node) this.gain.setNode(getAudioContext().createGain());
    this.gain.node!.gain.value = volume;
  }
  replaceSource(source: AudioSource) {
    const audioNode = createAudioNode(source);
    if (audioNode) this.source.replaceNode(audioNode);
  }
  get track() {
    return this.stream?.getAudioTracks()[0];
  }
  get stream() {
    return this.destination.node?.stream;
  }
}
export class MixAudioPipeline extends AudioPipeline {
  aec = new AudioNodeConnector('aec');
  mixNode = new AudioNodeConnector('mixNode');
  silentNode = new AudioNodeConnector('silentNode'); // 自动补静音帧
  denoiser = new AudioNodeConnector('denoiser');
  voiceChanger = new AudioNodeConnector<AudioNode>('voiceChanger');
  constructor(public context: MixContext) {
    super();
    this.source.pipeTo(this.aec).pipeTo(this.denoiser)
      .pipeTo(this.voiceChanger)
      .pipeTo(this.gain)
      .pipeTo(this.destination);
    this.silentNode.pipeTo(this.mixNode).pipeTo(this.aec, 1);
  }
  get isProcessEnabled() {
    return this.aec.node || this.denoiser.node || this.voiceChanger.node || this.gain.node;
  }
  connect() {
    if (this.context.inputs.has(this)) return;
    const prevSize = this.context.inputs.size;
    this.destination.setNode(this.context.destination);
    this.source.setContext(this.context);
    this.aec.setContext(this.context);
    this.silentNode.setContext(this.context);
    this.mixNode.setContext(this.context);
    this.denoiser.setContext(this.context);
    this.voiceChanger.setContext(this.context);
    this.gain.setContext(this.context);
    this.context.inputs.add(this);
    if (prevSize <= 1 && this.context.inputs.size > 1) {
      this.context.onAudioMixedChanged(true);
    }
  }
  disconnect() {
    if (!this.context.inputs.has(this)) return;
    const prevSize = this.context.inputs.size;
    this.destination.deleteNode();
    this.source.removeContext();
    this.aec.removeContext();
    this.silentNode.removeContext();
    this.mixNode.removeContext();
    this.denoiser.removeContext();
    this.voiceChanger.removeContext();
    this.gain.removeContext();
    this.context.inputs.delete(this);
    if (prevSize > 1 && this.context.inputs.size <= 1) {
      this.context.onAudioMixedChanged(false);
    }
  }
  remove() {
    this.gain.deleteNode();
    this.aec.deleteNode();
    this.silentNode.deleteNode();
    this.mixNode.deleteNode();
    this.denoiser.deleteNode();
    this.voiceChanger.deleteNode();
    this.source.deleteNode();
    this.disconnect();
  }
  setVolume(volume: number) {
    if (volume === 1) {
      if (this.gain.node) this.gain.deleteNode();
      return;
    }
    if (!this.gain.node) this.gain.setNode(this.context.audioContext.createGain());
    this.gain.node!.gain.value = volume;
  }
}

export abstract class MixContext {
  audioContext = getAudioContext('audio-mixer');
  destination = this.audioContext.createMediaStreamDestination();
  inputs = new Set<MixAudioPipeline>();
  mixWeight = 0; // 用于判断是否需要混音，>1 需要混音(即有多个音频源，或者一个音频源包含了效果器)
  constructor() {
    this.destination.channelCount = 1;
  }
  abstract mixOnChange(): void;
  abstract onAudioMixedChanged(isAudioMixed: boolean): void;
  addMixWeight(delta = 1) {
    this.mixWeight += delta;
    // delta == 1 时，mixWeight 从 1 到 2，表示需要混音, 触发 mixOnChange;delta==-1 时，mixWeight 从 2 到 1，表示不需要混音，触发 mixOnChange
    if (this.mixWeight - 1 === (delta + 1 >> 1)) this.mixOnChange();
  }
  reduceMixWeight(delta = 1) {
    this.addMixWeight(-delta);
  }
  close() {
    this.inputs.forEach(data => data.remove());
  }
  get mixTrack() {
    return this.destination.stream.getAudioTracks()[0];
  }
}
export const audioNodeCache = new WeakMap<AudioSource, AudioNode>();
export function createAudioNode(source: AudioSource) {
  try {
    let result = audioNodeCache.get(source);
    if (result) return result;
    const ctx = getAudioContext();
    if (source instanceof HTMLAudioElement) {
      result = ctx.createMediaElementSource(source);
    } else if (source instanceof MediaStreamTrack) {
      result = ctx.createMediaStreamSource(new MediaStream([source]));
    } else return source;
    audioNodeCache.set(source, result);
    return result;
  } catch (error) {
    if (IS_FIREFOX && error instanceof Error && error.name === 'NotSupportedError') {
      loggerManager.warn(error);
    } else {
      throw error;
    }
  }
}
